import { useState, useEffect } from 'react';
import { usePsychologistContext } from '@/contexts/PsychologistContext';
import { detectUserTimezone, normalizeTimezone, haveSameWallClockAllYear } from '@/utils/timezone';

const SESSION_SKIP_KEY = 'psi-tz-notice-skip-session';
const KEEP_KEY = 'psi-tz-notice-keep';

// Normaliza además las zonas argentinas a Buenos Aires: sin eso, una psicóloga con el
// navegador en "America/Argentina/Cordoba" y el perfil en Buenos Aires dispara el aviso de
// "¿Cambiaste de ubicación?" (y su email) por una diferencia de string, con la misma hora.
function canonicalize(tz: string): string {
  try {
    return normalizeTimezone(new Intl.DateTimeFormat('en', { timeZone: tz }).resolvedOptions().timeZone);
  } catch {
    return normalizeTimezone(tz);
  }
}

export function useTimezoneDetection() {
  const { psychologist } = usePsychologistContext();

  const browserTz = canonicalize(detectUserTimezone());
  const configuredTzRaw = psychologist?.timezone ?? (() => {
    try { return localStorage.getItem('psi-timezone'); } catch { return null; }
  })();
  const configuredTz = configuredTzRaw ? canonicalize(configuredTzRaw) : null;

  // La clave incluye ambas zonas: si el configured cambia, la clave no coincide y vuelve a mostrar
  const mismatchKey = configuredTz ? `${browserTz}|${configuredTz}` : null;

  const [dismissed, setDismissed] = useState(() => {
    try {
      if (sessionStorage.getItem(SESSION_SKIP_KEY)) return true;
      return mismatchKey !== null && localStorage.getItem(KEEP_KEY) === mismatchKey;
    } catch { return false; }
  });

  // Si cambia la combinación de mismatch, resetear dismissed (ignora SESSION_SKIP_KEY: esa solo aplica al load inicial)
  useEffect(() => {
    try {
      if (mismatchKey === null || localStorage.getItem(KEEP_KEY) === mismatchKey) return;
      setDismissed(false);
    } catch { /* noop */ }
  }, [mismatchKey]);

  const dismiss = () => {
    setDismissed(true);
  };

  const dismissForSession = () => {
    try { sessionStorage.setItem(SESSION_SKIP_KEY, '1'); } catch { /* noop */ }
    setDismissed(true);
  };

  // Guarda la combinación browser+configured: solo suprime este mismatch específico
  const dismissKeep = () => {
    if (mismatchKey) {
      try { localStorage.setItem(KEEP_KEY, mismatchKey); } catch { /* noop */ }
    }
    setDismissed(true);
  };

  return {
    browserTz,
    configuredTz,
    // El aviso existe para que los rangos horarios del calendario no queden desfasados. Si la
    // zona del navegador marca la misma hora que la configurada durante todo el año (ej. la
    // psicóloga se muda de Argentina a Uruguay), la agenda se sigue viendo bien y el cartel
    // sería solo ruido, aunque el país sea otro. Se compara por hora real, no por nombre de zona.
    shouldShow: !!configuredTz && !haveSameWallClockAllYear(browserTz, configuredTz) && !dismissed,
    dismiss,
    dismissForSession,
    dismissKeep,
  };
}
